/**
 * AR controller — the single source of truth for "View in your space".
 *
 * ── Why this exists ───────────────────────────────────────────────────────────
 * iOS AR (Quick Look) CANNOT open a GLB; it needs a .usdz. The old production
 * viewer used <model-viewer>, which converted the GLB → USDZ invisibly on every AR
 * tap — which is why AR "just worked from the GLB" without anyone baking a USDZ.
 * The configurator dropped model-viewer (a 2nd WebGL context on top of our engine
 * crashed Safari), and with it that invisible converter. This controller restores
 * the SAME mechanism using three's USDZExporter, straight from the live scene:
 *   - iOS  → generate a USDZ from the on-screen assembly (exact shape+fabric+legs),
 *            keep it ready, hand it to Quick Look on tap.
 *   - Android → Scene Viewer fetches a real GLB URL (separate app, can't read an
 *            in-page blob), so it uses the product's module GLB — derived
 *            automatically from config, no AR-specific fields.
 *   - Desktop → QR (unchanged).
 *
 * Result: a NEW product needs ZERO AR wiring. Drop a GLB in the config's modules,
 * AR works. No arModel/arModelIos fields, no pre-baked per-shape USDZ, no rebake
 * on every catalog change.
 *
 * iOS Quick Look requires the file at the SYNCHRONOUS tap (an async generate would
 * lose the user-gesture), so the USDZ is regenerated in the BACKGROUND after every
 * content change (shape/fabric/leg) and kept ready as a blob URL.
 */

import { launchAR, prepareAR, maybeAutoLaunchAR } from "./ArLauncher";
import { showToast } from "./Toast";
import { isMobileDevice } from "@/perf/GPUTier";
import type { Engine } from "@/engine/Engine";
import type { ProductConfig } from "@/types";

export interface ArController {
  /** Synchronous tap handler for the "View in your space" menu item. */
  launch(): void;
  /** Fire AR on arrival if we came from a desktop QR scan (?neovaa_ar=auto). */
  maybeAutoLaunch(): void;
  /** Whether AR is available for a given slug — drives AR-button visibility. The
   *  live controller exports any model on the fly (always true); the baked
   *  controller only has AR for configs with a pre-baked USDZ. */
  hasAr(slug: string): boolean;
  /** Release the in-memory USDZ blob URL. */
  dispose(): void;
}

function absUrl(u: string): string {
  try { return new URL(u, location.href).toString(); }
  catch { return u; }
}

/** Android Scene Viewer needs a fetchable https GLB (it runs in a separate app and
 *  can't read this page's blob URL). Derive it from the product's modules — the
 *  representative product GLB, automatically, with no AR-specific config. */
function deriveAndroidGlb(product: ProductConfig): string | undefined {
  const mod = product.modules.find((m) => m.glb);
  return mod?.glb ? absUrl(mod.glb) : undefined;
}

export function createArController(engine: Engine, product: ProductConfig): ArController {
  const androidGlb = deriveAndroidGlb(product);

  let usdzUrl: string | null = null;
  let generating = false;
  let dirty = true; // the model changed (or never generated) → needs a fresh USDZ
  let debounce: ReturnType<typeof setTimeout> | null = null;
  /** Armed by a tap that found no fresh USDZ: auto-launch Quick Look the moment
   *  generation lands (Alex 2026-06-11: ONE tap, never tap-again). Programmatic
   *  rel=ar anchor clicks after an async wait DO open Quick Look — this is
   *  exactly model-viewer's proven flow. */
  let pendingLaunch = false;
  /** Tap timestamp, set when an AR tap kicks off generation — so we can report the real
   *  tap→ready time (the on-device USDZ build is the variable cost). */
  let tapAt = 0;
  /** Surface the measured tap→ready time: console + window var always; a visible toast on
   *  the 360 page (body.spin360-on) so Alex can read the number straight off the phone. */
  function reportArTime(): void {
    if (!tapAt) return;
    const ms = performance.now() - tapAt; tapAt = 0;
    (window as unknown as { __NEOVAA_AR_MS__?: number }).__NEOVAA_AR_MS__ = Math.round(ms);
    console.info(`[ar] tap→ready in ${ms.toFixed(0)}ms`);
    if (typeof document !== "undefined" && document.body.classList.contains("spin360-on")) {
      showToast(`AR ready · ${(ms / 1000).toFixed(1)}s`);
    }
  }

  /** The GLB that matches what's ON SCREEN right now (live colorway × forme ×
   *  device tier) — Android Scene Viewer and the desktop QR must follow the
   *  configured product, not the config-time base model. */
  const liveGlb = () => engine.getLiveGlbUrl() ?? androidGlb;

  /** Regenerate the iOS USDZ from the CURRENT on-screen assembly and keep it ready.
   *  Best-effort: a failure leaves the previous URL in place (and surfaces a toast
   *  when a launch was pending). */
  async function regenerate(): Promise<void> {
    if (generating || !dirty) return;
    generating = true;
    try {
      // If the progressive-LOD proxy is still on screen, wait (bounded) for the
      // full/mobile tier — exporting now would ship a 256px USDZ into the room.
      const lodStage = () => (window as unknown as { __NEOVAA_LOD_STAGE__?: string }).__NEOVAA_LOD_STAGE__;
      for (let i = 0; i < 80 && lodStage() === "proxy"; i++) await new Promise((r) => setTimeout(r, 250));
      const blob = await engine.exportAssemblyUSDZ();
      if (blob) {
        const next = URL.createObjectURL(blob);
        if (usdzUrl) URL.revokeObjectURL(usdzUrl);
        usdzUrl = next;
        dirty = false;
      }
    } catch (e) {
      console.warn("[ar] USDZ generation failed; AR will retry on next change", e);
      if (pendingLaunch) { pendingLaunch = false; showToast("AR isn't available right now — try again"); }
    } finally {
      generating = false;
      if (dirty) {
        // A change landed mid-generation. Continue ONLY when a tap is waiting
        // (tap-driven work) or on desktop — never as mobile background work.
        if (pendingLaunch) void regenerate();
        else scheduleRegen();
      } else if (pendingLaunch) {
        // Fresh USDZ for the current model + a shopper waiting on their tap →
        // open Quick Look NOW (single-tap contract).
        pendingLaunch = false;
        reportArTime();
        launchAR({ glb: liveGlb(), usdz: usdzUrl ?? undefined })();
      }
    }
  }

  /** Mobile rule (2026-06-11, hardened after Alex's cold-load crash): NEVER
   *  export in the background on a phone, for ANY model. The USDZ export
   *  re-encodes every texture of the displayed model through canvases on the
   *  main thread — a multi-hundred-MB transient on top of the resident model.
   *  The earlier ">1 GB only" gate still let a ~800 MB seater export fire ~5s
   *  after the cold load (right while Alex was orbiting) → iOS killed the tab.
   *  On mobile the USDZ is generated ON THE AR TAP ONLY: launch() shows
   *  "Preparing AR…" and retries — a one-tap delay, never a crash. */
  function backgroundRegenAllowed(): boolean {
    return !isMobileDevice();
  }

  function scheduleRegen(): void {
    if (debounce) clearTimeout(debounce);
    // Debounce: rapid taps shouldn't each kick a full export (desktop path).
    debounce = setTimeout(() => {
      if (!backgroundRegenAllowed()) return; // mobile: stays dirty → generated on AR tap
      void regenerate();
    }, 350);
  }

  // Invalidate + regenerate on every shape/fabric/leg change so the ready USDZ
  // always matches what's displayed.
  engine.onContentChanged(() => {
    dirty = true;
    scheduleRegen();
  });

  // Android prefetch only (data-conscious WiFi gate lives in prepareAR). The iOS
  // USDZ is generated locally, so there's nothing to pre-download for iOS.
  prepareAR({ glb: androidGlb });

  return {
    launch() {
      // sources resolve LIVE: iOS uses the in-memory USDZ of the CURRENT model,
      // Android the currently-displayed GLB, desktop the QR modal.
      const isIOSLike = /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      if (isIOSLike && (dirty || !usdzUrl)) {
        // No USDZ yet OR the model changed since the last one (Alex 2026-06-11:
        // "every time I click on another AR it should prepare the AR related to
        // the product"). Prepare the CURRENT model, then auto-launch when ready
        // (pendingLaunch) — one tap, never two. Unchanged model → instant path
        // below with the cached USDZ.
        showToast("Preparing AR…");
        tapAt = performance.now();
        pendingLaunch = true;
        void regenerate();
        return;
      }
      launchAR({ glb: liveGlb(), usdz: usdzUrl ?? undefined })();
    },
    maybeAutoLaunch() {
      maybeAutoLaunchAR({ glb: liveGlb(), usdz: usdzUrl ?? undefined });
    },
    // The live exporter can build a USDZ for ANY on-screen model → AR always available.
    hasAr() {
      return true;
    },
    dispose() {
      if (debounce) clearTimeout(debounce);
      if (usdzUrl) URL.revokeObjectURL(usdzUrl);
      usdzUrl = null;
    },
  };
}
